import React, { useState, useMemo } from "react";
import { formatUSD } from "../../shared/utils";
import { CONDITIONS, PSA_GRADES, GRADED_ENABLED, isOversizeCard } from "../../shared/constants";
import { CardZoom, CardLinks } from "../../ui";

const SORTS = [
  ["added", "Added"],
  ["value", "Value"],
  ["name", "Name"],
  ["set", "Set"],
];

export default function Collection({
  collection,
  updateQty,
  updateCondition,
  removeFromCollection,
  toggleSlab,
  updatePsaGrade,
  getCardPrice,
  setOwnPrice,
  totalValue,
  unpricedCount = 0,
  percentage,
  setPercentage,
}) {
  /* Both games sit in one collection, so this is a per-card question rather
     than a per-screen one: a One Piece card has no condition pricing and no
     graded sales anywhere free, so it gets a quantity and nothing else. */
  const canGrade = (card) => (card.game || "pokemon") !== "onepiece";
  /* Conditions are a Pokemon thing; slabs are a Pokemon thing *and* a graded
     source thing, and the graded source is off for launch. A card slabbed
     before the switch is shown raw rather than showing a grade picker for
     prices that will not come. */
  const canSlab = (card) => GRADED_ENABLED && canGrade(card);
  const slabbed = (card) => canSlab(card) && card.isSlab;
  const [sort, setSort] = useState("added");
  const [setFilter, setSetFilter] = useState("ALL");
  const [hideOversize, setHideOversize] = useState(false);
  const [zoom, setZoom] = useState(null);

  // Null when nobody knows; the sort and the total both treat that as "not
  // counted" rather than as free.
  const priceOf = (card) =>
    getCardPrice ? getCardPrice(card) : (card.prices?.[card.condition]?.price ?? card.marketPrice ?? null);
  const priceOrZero = (card) => priceOf(card) ?? 0;

  // Only the sets actually represented in the collection, so the control never
  // offers a set that would empty the grid.
  const setsPresent = useMemo(() => {
    const seen = [];
    (collection || []).forEach((c) => {
      if (c.setName && !seen.includes(c.setName)) seen.push(c.setName);
    });
    return seen.sort((a, b) => a.localeCompare(b));
  }, [collection]);

  const oversizeCount = useMemo(
    () => (collection || []).filter(isOversizeCard).length,
    [collection]
  );

  const sorted = useMemo(() => {
    let list = [...(collection || [])];
    if (setFilter !== "ALL") list = list.filter((c) => c.setName === setFilter);
    if (hideOversize) list = list.filter((c) => !isOversizeCard(c));
    // Value sorts on the line total, not the unit price — two copies of a $40
    // card outweigh one $60 card when you're deciding what to haggle over.
    if (sort === "value") return list.sort((a, b) => priceOrZero(b) * b.qty - priceOrZero(a) * a.qty);
    if (sort === "name") return list.sort((a, b) => (a.name || "").localeCompare(b.name || ""));
    if (sort === "set")
      return list.sort(
        (a, b) =>
          (a.setName || "").localeCompare(b.setName || "") ||
          String(a.number || "").localeCompare(String(b.number || ""), undefined, { numeric: true })
      );
    return list; // "added" keeps insertion order
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [collection, sort, setFilter, hideOversize, getCardPrice]);

  const isFiltered = setFilter !== "ALL" || hideOversize;
  // The total follows what's on screen: filtering to one dealer's set and then
  // reading the whole collection's value would be the wrong number to haggle on.
  const shownValue = useMemo(
    () => sorted.reduce((sum, c) => sum + priceOrZero(c) * c.qty, 0),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [sorted, getCardPrice]
  );

  if (!collection?.length) return null;
  return (
    <>
      <h2 className="section-title">Your Collection</h2>

      <div className="sort-row">
        <span className="sort-label">Sort</span>
        {SORTS.map(([key, label]) => (
          <button
            key={key}
            className={sort === key ? "wl-chip wl-chip--active" : "wl-chip"}
            aria-pressed={sort === key}
            onClick={() => setSort(key)}
          >
            {label}
          </button>
        ))}
      </div>

      {(setsPresent.length > 1 || oversizeCount > 0) && (
        <div className="sort-row">
          <span className="sort-label">Filter</span>
          {setsPresent.length > 1 && (
            <select
              className="set-select"
              value={setFilter}
              onChange={(e) => setSetFilter(e.target.value)}
              aria-label="Filter by set"
            >
              <option value="ALL">All sets</option>
              {setsPresent.map((s) => (
                <option key={s} value={s}>{s}</option>
              ))}
            </select>
          )}
          {oversizeCount > 0 && (
            <label className="check-inline">
              <input
                type="checkbox"
                checked={hideOversize}
                onChange={(e) => setHideOversize(e.target.checked)}
              />
              Hide oversize ({oversizeCount})
            </label>
          )}
        </div>
      )}

      {sorted.length === 0 ? (
        <p className="msg-empty">Nothing here under this filter</p>
      ) : (
      <div className="card-grid card-grid--collection">
        {sorted.map((card) => {
          const displayPrice = getCardPrice ? getCardPrice(card) : (card.prices?.[card.condition]?.price ?? card.marketPrice ?? null);
          /* No quote for this condition, and no figure from the owner either.
             This used to show a percentage of the Near Mint price with a small
             "est." beside it. The tag was honest; the number was not. */
          const unpriced = displayPrice === null;
          const isOwn = !unpriced && !slabbed(card) && Number(card.ownPrice) > 0 &&
            !(card.prices?.[card.condition]?.estimated === false);
          return (
            <div key={card.id} className="card col-tile" style={{ background: slabbed(card) ? "var(--bg-slab)" : undefined }}>
              {/* Thumbnail beside the text, not above it */}
              <div className="col-tile-top">
                <button
                  type="button"
                  className="col-thumb-btn"
                  onClick={() => setZoom(card)}
                  title={`Enlarge ${card.name}`}
                  aria-label={`Enlarge ${card.name}`}
                >
                  <img className="card-image col-thumb" src={card.imageUrl} alt={card.name} />
                </button>
                <div className="col-details">
                  <div className="card-name">{card.name}</div>
                  <div className="card-number">
                    {card.setName ? `${card.setName} · ` : ""}#{card.number}
                  </div>
                  {/* Both games share one collection now, so each card says
                      which it is — otherwise a One Piece card with no
                      condition control just looks like a broken tile. */}
                  {!canGrade(card) && <span className="col-game">One Piece</span>}
                  {unpriced ? (
                    /* Asked for, not invented. Somebody holding the card can
                       judge a crease; a constant cannot. */
                    <label className="card-price card-price--ask">
                      <span className="card-price-ask-label">No {card.condition} quote — your price</span>
                      <span className="card-price-ask-field">
                        <span aria-hidden="true">$</span>
                        <input
                          type="text"
                          inputMode="decimal"
                          className="card-price-input"
                          placeholder="0.00"
                          aria-label={`Your price for ${card.name} in ${card.condition} condition`}
                          onBlur={(e) => setOwnPrice?.(card.id, e.target.value)}
                          onKeyDown={(e) => { if (e.key === "Enter") e.currentTarget.blur(); }}
                        />
                      </span>
                    </label>
                  ) : (
                    <div className="card-price">
                      {formatUSD(displayPrice)}
                      {isOwn && (
                        <button
                          type="button"
                          className="price-estimated price-own"
                          title="A price you set. Click to change it."
                          onClick={() => setOwnPrice?.(card.id, "")}
                        >
                          YOURS
                        </button>
                      )}
                    </div>
                  )}
                  {/* The screen where somebody is most likely holding the card,
                      and it had no way to look it up. */}
                  <CardLinks card={card} />

                  {/* Slab mode falls back to the raw price when there's no
                      graded sale to quote, which otherwise looks like the
                      toggle did nothing at all. Say which case it is. */}
                  {slabbed(card) && card.psaState === "loading" && (
                    <span className="col-note">CHECKING EBAY SALES…</span>
                  )}
                  {slabbed(card) && card.psaState !== "loading" &&
                    !Number.isFinite(Number(card.psaPrices?.[card.psaGrade])) && (
                      <span className="col-note">
                        NO EBAY SALES AT PSA {card.psaGrade || 10} · SHOWING RAW
                      </span>
                    )}
                </div>
              </div>

              {/* One connected control strip: raw/slab, grade or condition, qty.
                  One Piece has neither a graded price source nor condition
                  pricing, so the strip is just a quantity there — showing dead
                  controls would be worse than showing none. */}
              <div className="col-strip">
                {canSlab(card) && (
                <button
                  className={card.isSlab ? "col-seg col-seg--on" : "col-seg"}
                  onClick={() => toggleSlab(card.id)}
                  title="Switch between raw and slabbed pricing"
                >
                  {card.isSlab ? "SLAB" : "RAW"}
                </button>
                )}

                {!canGrade(card) ? null : slabbed(card) ? (
                  <select
                    className="col-seg col-seg--grow"
                    value={card.psaGrade || 10}
                    onChange={(e) => updatePsaGrade(card.id, e.target.value)}
                    aria-label={`PSA grade for ${card.name}`}
                  >
                    {PSA_GRADES.slice().reverse().map((g) => (
                      <option key={g} value={g}>PSA {g}</option>
                    ))}
                  </select>
                ) : (
                  <select
                    className="col-seg col-seg--grow"
                    value={card.condition}
                    onChange={(e) => updateCondition(card.id, e.target.value)}
                    aria-label={`Condition for ${card.name}`}
                  >
                    {CONDITIONS.map((c) => (
                      <option key={c} value={c}>{c}</option>
                    ))}
                  </select>
                )}

                <input
                  className="col-seg col-seg--qty"
                  type="number"
                  value={card.qty}
                  onChange={(e) => updateQty(card.id, Number(e.target.value))}
                  min={1}
                  max={99}
                  aria-label={`Quantity of ${card.name}`}
                />
              </div>

              <button className="btn btn-remove" onClick={() => removeFromCollection(card.id)}>Remove</button>
            </div>
          );
        })}
      </div>
      )}

      {/* Two columns: market on the left, muted; what you'd pay on the right,
          which is the number you're actually acting on. */}
      <div className="total-panel total-panel--split">
        <div className="total-col">
          <p className="total-label">{isFiltered ? "Value of shown cards" : "Collection Value"}</p>
          <p className="total-value">${shownValue.toFixed(2)}</p>
          {isFiltered && (
            <p className="total-note">
              {sorted.length} of {collection.length} cards · whole collection {formatUSD(totalValue)}
            </p>
          )}
          {/* Said, because a total that silently leaves cards out reads as
              the value of the collection when it is the value of part of it. */}
          {unpricedCount > 0 && (
            <p className="total-note total-note--warn">
              {unpricedCount} card{unpricedCount === 1 ? "" : "s"} not counted — no price for that condition. Set one on the card.
            </p>
          )}
        </div>

        <div className="total-col">
          <div className="percentage-row">
            {[75, 80, 85].map((p) => (
              <button
                key={p}
                className={percentage === p ? "percentage-btn percentage-btn--active" : "percentage-btn"}
                onClick={() => setPercentage(p)}
              >
                {p}%
              </button>
            ))}
            <input
              className="percentage-input"
              type="number"
              value={percentage}
              onChange={(e) => setPercentage(Number(e.target.value))}
              aria-label="Percentage of market to pay"
            />
          </div>
          <p className="offer-label">What to pay at {percentage}% of market</p>
          <p className="offer-price">${(shownValue * (percentage / 100)).toFixed(2)}</p>
        </div>
      </div>

      <CardZoom card={zoom} onClose={() => setZoom(null)} />
    </>
  );
}
